import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import {
  getMyStore,
  getStoreCustomerCredits,
  getOrCreateCustomerCreditAccount,
  registerCustomerCreditMovement,
  getCustomerCreditMovements,
  getStoreRecentCreditMovements,
  toggleCustomerCreditAccountActive,
  searchUsersForCredit,
  StoreCustomerCredit,
  StoreCustomerCreditMovement,
  CustomerCreditMovementType,
  CustomerCreditUser
} from '../../services/supabaseService';

const CREDIT_REASONS = [
  'Premiação de Torneio',
  'Venda de Cartas (Buylist)',
  'Depósito / Pagamento Antecipado (Pix/Dinheiro)',
  'Reembolso / Compensação de Pedido',
  'Bonificação de Fidelidade / Promoção',
  'Ajuste de Saldo'
];

const DEBIT_REASONS = [
  'Compra / Resgate no Balcão',
  'Inscrição em Torneio',
  'Abatimento em Pedido Online',
  'Devolução / Estorno de Saldo',
  'Ajuste de Saldo'
];

export const ManageCustomerCredits: React.FC = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  // Loja e Fichas
  const [store, setStore] = useState<any>(null);
  const [credits, setCredits] = useState<StoreCustomerCredit[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'positive_only' | 'zero_only' | 'inactive'>('all');
  const [sortBy, setSortBy] = useState<'balance_desc' | 'balance_asc' | 'name_asc' | 'recent'>('balance_desc');

  // Modal de Movimentação (Crédito / Débito)
  const [movementModalOpen, setMovementModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<StoreCustomerCredit | null>(null);
  const [movementType, setMovementType] = useState<CustomerCreditMovementType>('credit');
  const [movementAmount, setMovementAmount] = useState<string>('');
  const [movementReason, setMovementReason] = useState<string>('');
  const [customReason, setCustomReason] = useState<string>('');
  const [submittingMovement, setSubmittingMovement] = useState(false);

  // Modal de Novo Cliente / Nova Conta
  const [newCustomerModalOpen, setNewCustomerModalOpen] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<CustomerCreditUser[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedNewUser, setSelectedNewUser] = useState<CustomerCreditUser | null>(null);
  const [initialCreditAmount, setInitialCreditAmount] = useState<string>('');
  const [initialCreditReason, setInitialCreditReason] = useState<string>('Saldo Inicial / Premiação');
  const [creatingAccount, setCreatingAccount] = useState(false);

  // Modal de Extrato do Cliente
  const [statementModalOpen, setStatementModalOpen] = useState(false);
  const [statementAccount, setStatementAccount] = useState<StoreCustomerCredit | null>(null);
  const [statementMovements, setStatementMovements] = useState<StoreCustomerCreditMovement[]>([]);
  const [loadingStatement, setLoadingStatement] = useState(false);

  // Modal de Extrato Geral da Loja
  const [storeAuditModalOpen, setStoreAuditModalOpen] = useState(false);
  const [storeMovements, setStoreMovements] = useState<StoreCustomerCreditMovement[]>([]);
  const [loadingStoreAudit, setLoadingStoreAudit] = useState(false);
  const [auditFilterType, setAuditFilterType] = useState<'all' | 'credit' | 'debit'>('all');

  // Carrega loja e contas
  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const storeData = await getMyStore();
      if (!storeData) {
        showNotification('Você precisa ter uma loja para gerenciar créditos de clientes.', 'info');
        navigate('/minha-loja');
        return;
      }
      setStore(storeData);

      const creditsData = await getStoreCustomerCredits(storeData.id);
      setCredits(creditsData);
    } catch (err: any) {
      console.error('Erro ao carregar dados de crédito:', err);
      showNotification('Erro ao carregar créditos da loja.', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate, showNotification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Busca de usuários com debounce para o modal de novo cliente
  useEffect(() => {
    if (!newCustomerModalOpen) return;
    if (!userSearchQuery || userSearchQuery.trim().length < 2) {
      setUserSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchingUsers(true);
      try {
        const results = await searchUsersForCredit(userSearchQuery);
        // Filtra para remover usuários que já possuem conta ativa (opcionalmente pode mantê-los para reabrir)
        setUserSearchResults(results);
      } catch (err) {
        console.error('Erro na busca de usuários:', err);
      } finally {
        setSearchingUsers(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [userSearchQuery, newCustomerModalOpen]);

  // KPIs
  const stats = useMemo(() => {
    const activeAccounts = credits.filter(c => c.active);
    const customersWithBalance = activeAccounts.filter(c => c.balance > 0);
    const totalBalance = customersWithBalance.reduce((acc, c) => acc + c.balance, 0);
    const maxBalanceAccount = customersWithBalance.reduce((max, c) => (c.balance > (max?.balance || 0) ? c : max), null as StoreCustomerCredit | null);

    return {
      totalCirculating: totalBalance,
      customersWithBalanceCount: customersWithBalance.length,
      activeAccountsCount: activeAccounts.length,
      maxBalance: maxBalanceAccount?.balance || 0,
      maxBalanceUser: maxBalanceAccount?.user?.fullname || maxBalanceAccount?.user?.username || '—'
    };
  }, [credits]);

  // Lista filtrada e ordenada
  const filteredCredits = useMemo(() => {
    let list = [...credits];

    // Status filter
    if (statusFilter === 'positive_only') {
      list = list.filter(c => c.active && c.balance > 0);
    } else if (statusFilter === 'zero_only') {
      list = list.filter(c => c.active && c.balance === 0);
    } else if (statusFilter === 'inactive') {
      list = list.filter(c => !c.active);
    }

    // Search filter
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      list = list.filter(c => {
        const name = c.user?.fullname?.toLowerCase() || '';
        const username = c.user?.username?.toLowerCase() || '';
        const email = c.user?.email_copy?.toLowerCase() || '';
        return name.includes(q) || username.includes(q) || email.includes(q);
      });
    }

    // Sort
    list.sort((a, b) => {
      if (sortBy === 'balance_desc') return b.balance - a.balance;
      if (sortBy === 'balance_asc') return a.balance - b.balance;
      if (sortBy === 'name_asc') {
        const nameA = (a.user?.fullname || a.user?.username || '').toLowerCase();
        const nameB = (b.user?.fullname || b.user?.username || '').toLowerCase();
        return nameA.localeCompare(nameB);
      }
      if (sortBy === 'recent') {
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      }
      return 0;
    });

    return list;
  }, [credits, statusFilter, searchTerm, sortBy]);

  // Abre modal de movimentação rápida
  const openMovementModal = (account: StoreCustomerCredit, type: CustomerCreditMovementType = 'credit') => {
    setSelectedAccount(account);
    setMovementType(type);
    setMovementAmount('');
    setMovementReason(type === 'credit' ? CREDIT_REASONS[0] : DEBIT_REASONS[0]);
    setCustomReason('');
    setMovementModalOpen(true);
  };

  // Abre extrato individual
  const openStatementModal = async (account: StoreCustomerCredit) => {
    setStatementAccount(account);
    setStatementModalOpen(true);
    setLoadingStatement(true);
    try {
      const moves = await getCustomerCreditMovements(account.id, 100);
      setStatementMovements(moves);
    } catch (err) {
      console.error('Erro ao buscar extrato do cliente:', err);
      showNotification('Não foi possível carregar o histórico.', 'error');
    } finally {
      setLoadingStatement(false);
    }
  };

  // Abre extrato geral da loja
  const openStoreAuditModal = async () => {
    if (!store) return;
    setStoreAuditModalOpen(true);
    setLoadingStoreAudit(true);
    try {
      const moves = await getStoreRecentCreditMovements(store.id, 100);
      setStoreMovements(moves);
    } catch (err) {
      console.error('Erro ao buscar extrato geral da loja:', err);
      showNotification('Não foi possível carregar o histórico recente.', 'error');
    } finally {
      setLoadingStoreAudit(false);
    }
  };

  // Submete movimentação via RPC
  const handleSubmitMovement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAccount) return;

    const parsedAmount = parseFloat(movementAmount.replace(',', '.'));
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      showNotification('Insira um valor válido maior que zero.', 'info');
      return;
    }

    if (movementType === 'debit' && parsedAmount > selectedAccount.balance) {
      showNotification(
        `O débito (R$ ${parsedAmount.toFixed(2)}) não pode ser maior que o saldo atual (R$ ${selectedAccount.balance.toFixed(2)}).`,
        'info'
      );
      return;
    }

    const finalReason = (movementReason === 'Outro' ? customReason : movementReason || customReason).trim();
    if (!finalReason) {
      showNotification('Informe o motivo da movimentação.', 'info');
      return;
    }

    setSubmittingMovement(true);
    try {
      const updated = await registerCustomerCreditMovement({
        creditAccountId: selectedAccount.id,
        movementType,
        amount: parsedAmount,
        reason: finalReason
      });

      // Atualiza lista local
      setCredits(prev =>
        prev.map(c => (c.id === updated.id ? { ...c, balance: updated.balance, updated_at: updated.updated_at } : c))
      );

      // Se o modal de extrato estiver aberto com essa conta, recarrega
      if (statementAccount?.id === selectedAccount.id) {
        const moves = await getCustomerCreditMovements(selectedAccount.id, 100);
        setStatementMovements(moves);
      }

      setMovementModalOpen(false);
      showNotification(
        movementType === 'credit'
          ? `Crédito de R$ ${parsedAmount.toFixed(2)} lançado com sucesso!`
          : `Débito de R$ ${parsedAmount.toFixed(2)} realizado com sucesso!`,
        'success'
      );
    } catch (err: any) {
      console.error('Erro ao registrar movimentação:', err);
      const msg = err.message || 'Erro ao registrar movimentação de crédito.';
      showNotification(msg, 'error');
    } finally {
      setSubmittingMovement(false);
    }
  };

  // Cria ou vincula cliente
  const handleCreateOrSelectUser = async () => {
    if (!selectedNewUser || !store) return;

    setCreatingAccount(true);
    try {
      const account = await getOrCreateCustomerCreditAccount(store.id, selectedNewUser.id);

      const parsedInitial = parseFloat(initialCreditAmount.replace(',', '.'));
      if (!isNaN(parsedInitial) && parsedInitial > 0) {
        await registerCustomerCreditMovement({
          creditAccountId: account.id,
          movementType: 'credit',
          amount: parsedInitial,
          reason: initialCreditReason.trim() || 'Crédito Inicial'
        });
      }

      showNotification(`Conta de crédito para @${selectedNewUser.username} configurada!`, 'success');
      setNewCustomerModalOpen(false);
      setSelectedNewUser(null);
      setUserSearchQuery('');
      setInitialCreditAmount('');
      loadData(true);
    } catch (err: any) {
      console.error('Erro ao abrir conta de crédito:', err);
      showNotification(err.message || 'Erro ao criar conta de crédito.', 'error');
    } finally {
      setCreatingAccount(false);
    }
  };

  // Alterna status Ativo/Inativo
  const handleToggleActive = async (account: StoreCustomerCredit) => {
    const actionName = account.active ? 'desativar' : 'reativar';
    const confirm = await Swal.fire({
      title: `Deseja ${actionName} esta conta?`,
      text: account.active
        ? `A conta de @${account.user?.username || 'cliente'} será pausada para novas movimentações.`
        : `A conta de @${account.user?.username || 'cliente'} voltará a ficar disponível para créditos e débitos.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: account.active ? '#ef4444' : '#10b981',
      cancelButtonColor: '#64748b',
      confirmButtonText: `Sim, ${actionName}`,
      cancelButtonText: 'Cancelar',
      background: '#0f172a',
      color: '#fff'
    });

    if (confirm.isConfirmed) {
      try {
        await toggleCustomerCreditAccountActive(account.id, !account.active);
        setCredits(prev => prev.map(c => (c.id === account.id ? { ...c, active: !c.active } : c)));
        showNotification(`Conta ${account.active ? 'desativada' : 'reativada'} com sucesso.`, 'success');
      } catch (err: any) {
        console.error('Erro ao alternar status da conta:', err);
        showNotification('Erro ao atualizar status da conta.', 'error');
      }
    }
  };

  // Cálculo de prévia do novo saldo no modal de movimentação
  const previewNewBalance = useMemo(() => {
    if (!selectedAccount) return 0;
    const current = selectedAccount.balance;
    const amount = parseFloat(movementAmount.replace(',', '.')) || 0;
    if (movementType === 'credit') return current + amount;
    return Math.max(0, current - amount);
  }, [selectedAccount, movementType, movementAmount]);

  const parsedAmountForValidation = parseFloat(movementAmount.replace(',', '.')) || 0;
  const isDebitOverLimit = movementType === 'debit' && selectedAccount && parsedAmountForValidation > selectedAccount.balance;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pb-16">
      {/* Header */}
      <div className="border-b border-slate-800/80 bg-slate-900/50 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                <Link to="/painel-lojista" className="hover:text-purple-400 transition-colors flex items-center gap-1">
                  <i className="fas fa-store text-purple-400"></i>
                  <span>Painel do Lojista</span>
                </Link>
                <span>/</span>
                <span className="text-slate-200 font-medium">Créditos de Clientes</span>
              </div>
              <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight flex items-center gap-2.5">
                <span className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 text-sm">
                  <i className="fas fa-coins"></i>
                </span>
                Créditos em Loja
              </h1>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <button
                id="btn-refresh-credits"
                onClick={() => loadData(true)}
                disabled={loading || refreshing}
                className="px-3 sm:px-4 py-2 rounded-xl bg-slate-900 border border-slate-800 hover:border-slate-700 text-slate-300 hover:text-white text-xs font-bold transition-all flex items-center gap-2"
                title="Recarregar dados"
              >
                <i className={`fas fa-arrows-rotate ${refreshing ? 'animate-spin text-purple-400' : ''}`}></i>
                <span className="hidden sm:inline">Atualizar</span>
              </button>

              <button
                id="btn-store-audit"
                onClick={openStoreAuditModal}
                className="px-3.5 sm:px-4 py-2 rounded-xl bg-slate-900 border border-slate-800 hover:border-purple-500/40 text-slate-200 hover:text-purple-300 text-xs font-bold transition-all flex items-center gap-2"
              >
                <i className="fas fa-receipt text-purple-400"></i>
                <span>Extrato da Loja</span>
              </button>

              <button
                id="btn-new-customer-credit"
                onClick={() => {
                  setSelectedNewUser(null);
                  setUserSearchQuery('');
                  setInitialCreditAmount('');
                  setNewCustomerModalOpen(true);
                }}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-black uppercase tracking-wider transition-all shadow-lg shadow-emerald-950/40 flex items-center gap-2"
              >
                <i className="fas fa-user-plus"></i>
                <span>Novo Crédito / Cliente</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Card 1: Total Circulante */}
          <div className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-4 sm:p-5 relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Total Circulante</span>
              <div className="w-8 h-8 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs">
                <i className="fas fa-wallet"></i>
              </div>
            </div>
            <div className="mt-2.5">
              <span className="text-xl sm:text-2xl font-black text-emerald-400 tracking-tight">
                R$ {stats.totalCirculating.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Saldo somado de todos os clientes</p>
          </div>

          {/* Card 2: Clientes com Saldo */}
          <div className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-4 sm:p-5 relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Clientes com Saldo</span>
              <div className="w-8 h-8 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 text-xs">
                <i className="fas fa-users"></i>
              </div>
            </div>
            <div className="mt-2.5">
              <span className="text-xl sm:text-2xl font-black text-white tracking-tight">
                {stats.customersWithBalanceCount}
              </span>
              <span className="text-xs text-slate-500 ml-1.5">de {stats.activeAccountsCount} ativos</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Possuem saldo &gt; R$ 0,00</p>
          </div>

          {/* Card 3: Maior Saldo Individual */}
          <div className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-4 sm:p-5 relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Maior Crédito</span>
              <div className="w-8 h-8 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 text-xs">
                <i className="fas fa-crown"></i>
              </div>
            </div>
            <div className="mt-2.5">
              <span className="text-xl sm:text-2xl font-black text-cyan-400 tracking-tight">
                R$ {stats.maxBalance.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1 truncate" title={stats.maxBalanceUser}>
              {stats.maxBalanceUser}
            </p>
          </div>

          {/* Card 4: Fichas Totais */}
          <div className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-4 sm:p-5 relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Fichas Registradas</span>
              <div className="w-8 h-8 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 text-xs">
                <i className="fas fa-address-book"></i>
              </div>
            </div>
            <div className="mt-2.5">
              <span className="text-xl sm:text-2xl font-black text-white tracking-tight">
                {credits.length}
              </span>
              <span className="text-xs text-slate-500 ml-1.5">contas</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Clientes vinculados à loja</p>
          </div>
        </div>

        {/* Filtros e Busca */}
        <div className="bg-slate-900/40 border border-slate-800/80 rounded-2xl p-3.5 sm:p-4 space-y-3">
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
            {/* Search Input */}
            <div className="relative flex-1">
              <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
              <input
                id="input-search-customers"
                type="text"
                placeholder="Buscar por nome, @username ou e-mail..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full bg-slate-950/80 border border-slate-800 focus:border-purple-500/50 rounded-xl pl-9 pr-8 py-2 text-xs text-white placeholder-slate-500 focus:outline-none transition-colors"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>

            {/* Status Pills */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0 scrollbar-none">
              <button
                id="filter-status-all"
                onClick={() => setStatusFilter('all')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  statusFilter === 'all'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'bg-slate-950/60 text-slate-400 hover:text-white border border-slate-800/80'
                }`}
              >
                Todos ({credits.length})
              </button>
              <button
                id="filter-status-positive"
                onClick={() => setStatusFilter('positive_only')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  statusFilter === 'positive_only'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-slate-950/60 text-slate-400 hover:text-emerald-400 border border-slate-800/80'
                }`}
              >
                Com Saldo ({stats.customersWithBalanceCount})
              </button>
              <button
                id="filter-status-zero"
                onClick={() => setStatusFilter('zero_only')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  statusFilter === 'zero_only'
                    ? 'bg-slate-700 text-white shadow-sm'
                    : 'bg-slate-950/60 text-slate-400 hover:text-white border border-slate-800/80'
                }`}
              >
                Zerados ({stats.activeAccountsCount - stats.customersWithBalanceCount})
              </button>
              <button
                id="filter-status-inactive"
                onClick={() => setStatusFilter('inactive')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  statusFilter === 'inactive'
                    ? 'bg-rose-900/60 text-rose-300 border border-rose-700/50'
                    : 'bg-slate-950/60 text-slate-400 hover:text-rose-400 border border-slate-800/80'
                }`}
              >
                Inativos ({credits.filter(c => !c.active).length})
              </button>
            </div>

            {/* Sort Select */}
            <div className="flex items-center gap-2 shrink-0">
              <label htmlFor="select-sort-credits" className="text-xs text-slate-400 whitespace-nowrap">
                Ordenar:
              </label>
              <select
                id="select-sort-credits"
                value={sortBy}
                onChange={e => setSortBy(e.target.value as any)}
                className="bg-slate-950/80 border border-slate-800 focus:border-purple-500/50 rounded-xl px-2.5 py-1.5 text-xs text-white focus:outline-none"
              >
                <option value="balance_desc">Maior Saldo</option>
                <option value="balance_asc">Menor Saldo</option>
                <option value="name_asc">Nome (A-Z)</option>
                <option value="recent">Atualizados Recentemente</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tabela / Lista de Clientes */}
        {loading ? (
          <div className="bg-slate-900/30 border border-slate-800/60 rounded-2xl p-12 text-center">
            <i className="fas fa-spinner fa-spin text-3xl text-purple-400 mb-3"></i>
            <p className="text-xs text-slate-400 font-medium">Carregando créditos da loja...</p>
          </div>
        ) : filteredCredits.length === 0 ? (
          <div className="bg-slate-900/30 border border-slate-800/60 rounded-2xl p-12 text-center max-w-md mx-auto">
            <div className="w-14 h-14 rounded-2xl bg-slate-800/80 flex items-center justify-center mx-auto mb-4 text-slate-500 text-2xl">
              <i className="fas fa-coins"></i>
            </div>
            <h3 className="text-sm font-bold text-white uppercase tracking-tight">Nenhuma ficha encontrada</h3>
            <p className="text-xs text-slate-400 mt-1 leading-relaxed">
              {searchTerm
                ? `Nenhum cliente corresponde ao filtro "${searchTerm}".`
                : 'Sua loja ainda não possui créditos registrados para clientes.'}
            </p>
            <button
              onClick={() => setNewCustomerModalOpen(true)}
              className="mt-4 px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all shadow-md inline-flex items-center gap-2"
            >
              <i className="fas fa-user-plus"></i>
              <span>Abrir Primeiro Crédito</span>
            </button>
          </div>
        ) : (
          <div className="bg-slate-900/40 border border-slate-800/80 rounded-2xl overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-800/80 bg-slate-950/60 text-[11px] uppercase tracking-wider text-slate-400 font-black">
                    <th className="py-3.5 px-4 sm:px-6">Cliente</th>
                    <th className="py-3.5 px-4">Status</th>
                    <th className="py-3.5 px-4 text-right">Saldo Atual</th>
                    <th className="py-3.5 px-4 text-center hidden md:table-cell">Última Atualização</th>
                    <th className="py-3.5 px-4 sm:px-6 text-right">Ações Rápidas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60 text-xs">
                  {filteredCredits.map(account => {
                    const hasPositive = account.balance > 0;
                    const u = account.user;
                    const displayName = u?.fullname || u?.username || 'Cliente';
                    const handle = u?.username ? `@${u.username}` : `ID: ${account.user_id}`;

                    return (
                      <tr
                        key={account.id}
                        className={`hover:bg-slate-850/40 transition-colors ${
                          !account.active ? 'opacity-60 bg-rose-950/5' : ''
                        }`}
                      >
                        {/* Cliente */}
                        <td className="py-3.5 px-4 sm:px-6">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700/80 overflow-hidden flex items-center justify-center shrink-0 text-slate-400 font-bold text-sm">
                              {u?.avatar ? (
                                <img
                                  src={u.avatar}
                                  alt={displayName}
                                  className="w-full h-full object-cover"
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                <span>{displayName.charAt(0).toUpperCase()}</span>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="font-bold text-white hover:text-purple-400 transition-colors truncate flex items-center gap-1.5">
                                <span className="truncate">{displayName}</span>
                                {!account.active && (
                                  <span className="text-[10px] font-bold text-rose-400 bg-rose-500/10 px-1.5 py-0.5 rounded border border-rose-500/20">
                                    Inativo
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-slate-400 flex items-center gap-2 truncate">
                                <span>{handle}</span>
                                {u?.email_copy && (
                                  <span className="text-slate-500 truncate hidden sm:inline">
                                    • {u.email_copy}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="py-3.5 px-4">
                          {account.active ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                              Ativa
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-800 text-slate-400 border border-slate-700">
                              Bloqueada
                            </span>
                          )}
                        </td>

                        {/* Saldo Atual */}
                        <td className="py-3.5 px-4 text-right">
                          <div className={`font-black text-sm sm:text-base ${hasPositive ? 'text-emerald-400' : 'text-slate-400'}`}>
                            R$ {account.balance.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div className="text-[10px] text-slate-500">
                            {hasPositive ? 'Disponível' : 'Sem saldo'}
                          </div>
                        </td>

                        {/* Última Atualização */}
                        <td className="py-3.5 px-4 text-center hidden md:table-cell text-slate-400 text-[11px]">
                          {account.updated_at
                            ? new Date(account.updated_at).toLocaleDateString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : '—'}
                        </td>

                        {/* Ações */}
                        <td className="py-3.5 px-4 sm:px-6 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* Creditar */}
                            <button
                              id={`btn-credit-${account.id}`}
                              onClick={() => openMovementModal(account, 'credit')}
                              disabled={!account.active}
                              title="Adicionar Crédito (+)"
                              className="px-2.5 py-1.5 rounded-xl bg-emerald-600/15 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-500/30 font-bold text-[11px] transition-all flex items-center gap-1 disabled:opacity-30"
                            >
                              <i className="fas fa-plus text-[10px]"></i>
                              <span className="hidden sm:inline">Creditar</span>
                            </button>

                            {/* Debitar */}
                            <button
                              id={`btn-debit-${account.id}`}
                              onClick={() => openMovementModal(account, 'debit')}
                              disabled={!account.active || account.balance <= 0}
                              title="Debitar / Resgatar (-)"
                              className="px-2.5 py-1.5 rounded-xl bg-amber-600/15 hover:bg-amber-600/30 text-amber-400 border border-amber-500/30 font-bold text-[11px] transition-all flex items-center gap-1 disabled:opacity-30"
                            >
                              <i className="fas fa-minus text-[10px]"></i>
                              <span className="hidden sm:inline">Debitar</span>
                            </button>

                            {/* Extrato do Cliente */}
                            <button
                              id={`btn-statement-${account.id}`}
                              onClick={() => openStatementModal(account)}
                              title="Ver Extrato de Movimentações"
                              className="w-8 h-8 rounded-xl bg-slate-800/80 hover:bg-purple-600/20 hover:text-purple-300 text-slate-300 border border-slate-700/80 flex items-center justify-center text-xs transition-all"
                            >
                              <i className="fas fa-file-invoice-dollar"></i>
                            </button>

                            {/* Menu de Configuração / Toggle */}
                            <button
                              id={`btn-toggle-${account.id}`}
                              onClick={() => handleToggleActive(account)}
                              title={account.active ? 'Bloquear conta' : 'Reativar conta'}
                              className="w-8 h-8 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white border border-slate-700/80 flex items-center justify-center text-xs transition-all"
                            >
                              <i className={`fas ${account.active ? 'fa-ban' : 'fa-check'}`}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Rodapé informativo */}
            <div className="p-3.5 bg-slate-950/60 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between text-[11px] text-slate-500 gap-2">
              <span>
                Exibindo <b className="text-slate-300">{filteredCredits.length}</b> de <b className="text-slate-300">{credits.length}</b> contas de crédito.
              </span>
              <span className="flex items-center gap-2">
                <i className="fas fa-shield-alt text-emerald-400"></i>
                Auditoria via RPC transacional ativa
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ========================================================= */}
      {/* MODAL: MOVIMENTAÇÃO RÁPIDA (CRÉDITO OU DÉBITO) */}
      {/* ========================================================= */}
      {movementModalOpen && selectedAccount && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/40">
              <div className="flex items-center gap-2.5">
                <div
                  className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold ${
                    movementType === 'credit'
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                      : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                  }`}
                >
                  <i className={`fas ${movementType === 'credit' ? 'fa-arrow-trend-up' : 'fa-arrow-trend-down'}`}></i>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white tracking-tight">
                    {movementType === 'credit' ? 'Lançar Crédito ao Cliente' : 'Registrar Débito / Resgate'}
                  </h3>
                  <p className="text-[11px] text-slate-400">Transação direta e auditada no saldo do cliente</p>
                </div>
              </div>
              <button
                onClick={() => setMovementModalOpen(false)}
                className="w-8 h-8 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center text-xs transition-colors"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmitMovement} className="p-6 space-y-5">
              {/* Card do Cliente & Saldo Atual */}
              <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 overflow-hidden flex items-center justify-center text-slate-300 font-bold">
                    {selectedAccount.user?.avatar ? (
                      <img
                        src={selectedAccount.user.avatar}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <span>{(selectedAccount.user?.fullname || selectedAccount.user?.username || 'C').charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-xs">
                      {selectedAccount.user?.fullname || selectedAccount.user?.username}
                    </h4>
                    <p className="text-[11px] text-slate-400">@{selectedAccount.user?.username}</p>
                  </div>
                </div>

                <div className="text-right">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block">Saldo Atual</span>
                  <span className="text-base font-black text-white">
                    R$ {selectedAccount.balance.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Seletor de Tipo (Crédito vs Débito) */}
              <div>
                <label className="text-xs font-bold text-slate-400 mb-2 block">Tipo de Movimentação</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setMovementType('credit');
                      setMovementReason(CREDIT_REASONS[0]);
                    }}
                    className={`p-3 rounded-2xl border text-xs font-bold flex items-center justify-center gap-2 transition-all ${
                      movementType === 'credit'
                        ? 'bg-emerald-600/20 border-emerald-500 text-emerald-400 shadow-md'
                        : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    <i className="fas fa-plus-circle text-sm"></i>
                    <span>Crédito (+ Adicionar)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setMovementType('debit');
                      setMovementReason(DEBIT_REASONS[0]);
                    }}
                    className={`p-3 rounded-2xl border text-xs font-bold flex items-center justify-center gap-2 transition-all ${
                      movementType === 'debit'
                        ? 'bg-amber-600/20 border-amber-500 text-amber-400 shadow-md'
                        : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    <i className="fas fa-minus-circle text-sm"></i>
                    <span>Débito (- Descontar)</span>
                  </button>
                </div>
              </div>

              {/* Input de Valor */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="input-movement-amount" className="text-xs font-bold text-slate-400">
                    Valor da Movimentação (R$) *
                  </label>
                  {movementType === 'debit' && selectedAccount.balance > 0 && (
                    <button
                      type="button"
                      onClick={() => setMovementAmount(selectedAccount.balance.toString())}
                      className="text-[11px] font-bold text-amber-400 hover:underline"
                    >
                      Usar Saldo Total (R$ {selectedAccount.balance.toFixed(2)})
                    </button>
                  )}
                </div>

                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-500">R$</span>
                  <input
                    id="input-movement-amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="0,00"
                    value={movementAmount}
                    onChange={e => setMovementAmount(e.target.value)}
                    required
                    className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 rounded-2xl pl-12 pr-4 py-3 text-lg font-black text-white placeholder-slate-600 focus:outline-none transition-colors"
                  />
                </div>

                {/* Atalhos de Valor */}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {[10, 20, 50, 100].map(val => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setMovementAmount(val.toString())}
                      className="px-2.5 py-1 rounded-lg bg-slate-800/70 hover:bg-slate-700 text-slate-300 text-[11px] font-bold transition-colors"
                    >
                      + R$ {val}
                    </button>
                  ))}
                </div>

                {/* Validação de limite de débito */}
                {isDebitOverLimit && (
                  <p className="text-[11px] text-rose-400 mt-2 flex items-center gap-1">
                    <i className="fas fa-triangle-exclamation"></i>
                    Valor excede o saldo disponível de R$ {selectedAccount.balance.toFixed(2)}.
                  </p>
                )}
              </div>

              {/* Prévia do Novo Saldo */}
              <div className="bg-slate-950/60 border border-slate-800/80 rounded-2xl p-3.5 flex items-center justify-between">
                <div>
                  <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block">Novo Saldo Previsto</span>
                  <span className="text-xs text-slate-400">Após a confirmação da transação</span>
                </div>
                <div className="text-right">
                  <span
                    className={`text-lg font-black ${
                      movementType === 'credit'
                        ? 'text-emerald-400'
                        : previewNewBalance > 0
                        ? 'text-amber-400'
                        : 'text-slate-400'
                    }`}
                  >
                    R$ {previewNewBalance.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Motivo da Operação */}
              <div>
                <label className="text-xs font-bold text-slate-400 mb-2 block">Motivo da Movimentação *</label>
                <div className="flex flex-wrap gap-1.5 mb-2.5">
                  {(movementType === 'credit' ? CREDIT_REASONS : DEBIT_REASONS).map(reason => (
                    <button
                      key={reason}
                      type="button"
                      onClick={() => {
                        setMovementReason(reason);
                        setCustomReason('');
                      }}
                      className={`px-2.5 py-1 rounded-xl text-[11px] font-medium transition-all ${
                        movementReason === reason
                          ? 'bg-purple-600 text-white shadow-sm'
                          : 'bg-slate-950/80 text-slate-400 hover:text-white border border-slate-800'
                      }`}
                    >
                      {reason}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setMovementReason('Outro')}
                    className={`px-2.5 py-1 rounded-xl text-[11px] font-medium transition-all ${
                      movementReason === 'Outro'
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'bg-slate-950/80 text-slate-400 hover:text-white border border-slate-800'
                    }`}
                  >
                    Outro motivo...
                  </button>
                </div>

                {movementReason === 'Outro' && (
                  <input
                    type="text"
                    placeholder="Descreva o motivo detalhado..."
                    value={customReason}
                    onChange={e => setCustomReason(e.target.value)}
                    required
                    className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 rounded-xl px-3.5 py-2 text-xs text-white placeholder-slate-500 focus:outline-none"
                  />
                )}
              </div>

              {/* Ações */}
              <div className="pt-2 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setMovementModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submittingMovement || isDebitOverLimit || parsedAmountForValidation <= 0}
                  className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider text-white shadow-lg transition-all flex items-center gap-2 disabled:opacity-40 ${
                    movementType === 'credit'
                      ? 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-950/40'
                      : 'bg-amber-600 hover:bg-amber-500 shadow-amber-950/40'
                  }`}
                >
                  {submittingMovement ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Processando...</span>
                    </>
                  ) : (
                    <>
                      <i className={`fas ${movementType === 'credit' ? 'fa-check' : 'fa-arrow-right'}`}></i>
                      <span>Confirmar {movementType === 'credit' ? 'Crédito' : 'Débito'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL: VINCULAR NOVO CLIENTE / ABRIR CONTA */}
      {/* ========================================================= */}
      {newCustomerModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/40">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">
                  <i className="fas fa-user-plus"></i>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white tracking-tight">Nova Ficha de Crédito</h3>
                  <p className="text-[11px] text-slate-400">Selecione o cliente cadastrado para vincular ou creditar</p>
                </div>
              </div>
              <button
                onClick={() => setNewCustomerModalOpen(false)}
                className="w-8 h-8 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center text-xs transition-colors"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Campo de Busca de Usuário */}
              <div>
                <label className="text-xs font-bold text-slate-400 mb-1.5 block">
                  Pesquisar Cliente na Plataforma *
                </label>
                <div className="relative">
                  <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
                  <input
                    type="text"
                    placeholder="Digite nome, @username ou e-mail..."
                    value={userSearchQuery}
                    onChange={e => {
                      setUserSearchQuery(e.target.value);
                      setSelectedNewUser(null);
                    }}
                    className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 rounded-xl pl-9 pr-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none transition-colors"
                  />
                  {searchingUsers && (
                    <i className="fas fa-spinner fa-spin absolute right-3.5 top-1/2 -translate-y-1/2 text-purple-400 text-xs"></i>
                  )}
                </div>
              </div>

              {/* Lista de Resultados da Busca */}
              {userSearchResults.length > 0 && !selectedNewUser && (
                <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1 border border-slate-800/80 rounded-xl p-2 bg-slate-950/60">
                  <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider px-2 block mb-1">
                    Resultados Encontrados ({userSearchResults.length})
                  </span>
                  {userSearchResults.map(u => (
                    <div
                      key={u.id}
                      onClick={() => setSelectedNewUser(u)}
                      className="p-2.5 rounded-xl hover:bg-purple-600/10 hover:border-purple-500/30 border border-transparent cursor-pointer transition-all flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold text-slate-300 overflow-hidden">
                          {u.avatar ? (
                            <img src={u.avatar} alt={u.username} className="w-full h-full object-cover" />
                          ) : (
                            <span>{u.username.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div>
                          <div className="text-xs font-bold text-white group-hover:text-purple-300">
                            {u.fullname || u.username}
                          </div>
                          <div className="text-[10px] text-slate-400">@{u.username}</div>
                        </div>
                      </div>
                      <span className="text-[11px] text-purple-400 font-bold group-hover:translate-x-0.5 transition-transform">
                        Selecionar <i className="fas fa-chevron-right text-[9px] ml-1"></i>
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Cliente Selecionado */}
              {selectedNewUser && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-950 border border-emerald-500/40 flex items-center justify-center text-emerald-300 font-bold overflow-hidden">
                      {selectedNewUser.avatar ? (
                        <img src={selectedNewUser.avatar} alt="Avatar" className="w-full h-full object-cover" />
                      ) : (
                        <span>{selectedNewUser.username.charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div>
                      <div className="text-xs font-bold text-white flex items-center gap-2">
                        <span>{selectedNewUser.fullname || selectedNewUser.username}</span>
                        <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.2 rounded">
                          Selecionado
                        </span>
                      </div>
                      <div className="text-[11px] text-emerald-400/80">@{selectedNewUser.username}</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedNewUser(null)}
                    className="text-xs text-slate-400 hover:text-white"
                  >
                    Trocar
                  </button>
                </div>
              )}

              {/* Crédito Inicial Opcional */}
              <div className="pt-2 border-t border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-400">Crédito Inicial (Opcional)</label>
                  <span className="text-[10px] text-slate-500">Deixe em branco para começar com R$ 0,00</span>
                </div>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-500">R$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0,00"
                    value={initialCreditAmount}
                    onChange={e => setInitialCreditAmount(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 rounded-xl pl-9 pr-4 py-2.5 text-xs text-white focus:outline-none"
                  />
                </div>

                {parseFloat(initialCreditAmount) > 0 && (
                  <div>
                    <label className="text-[11px] font-bold text-slate-400 mb-1 block">Motivo do Crédito Inicial</label>
                    <input
                      type="text"
                      value={initialCreditReason}
                      onChange={e => setInitialCreditReason(e.target.value)}
                      placeholder="Ex: Premiação do Torneio de Sábado"
                      className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 rounded-xl px-3 py-2 text-xs text-white focus:outline-none"
                    />
                  </div>
                )}
              </div>

              {/* Botões */}
              <div className="pt-3 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setNewCustomerModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={!selectedNewUser || creatingAccount}
                  onClick={handleCreateOrSelectUser}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-black uppercase tracking-wider transition-all shadow-lg flex items-center gap-2 disabled:opacity-40"
                >
                  {creatingAccount ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Criando Ficha...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check"></i>
                      <span>Vincular & Criar Ficha</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL: EXTRATO INDIVIDUAL DO CLIENTE */}
      {/* ========================================================= */}
      {statementModalOpen && statementAccount && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/40 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 overflow-hidden flex items-center justify-center text-slate-300 font-bold">
                  {statementAccount.user?.avatar ? (
                    <img
                      src={statementAccount.user.avatar}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span>{(statementAccount.user?.fullname || statementAccount.user?.username || 'C').charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <span>Extrato de Créditos: {statementAccount.user?.fullname || statementAccount.user?.username}</span>
                  </h3>
                  <p className="text-[11px] text-slate-400">@{statementAccount.user?.username} • ID da Ficha: #{statementAccount.id}</p>
                </div>
              </div>

              <button
                onClick={() => setStatementModalOpen(false)}
                className="w-8 h-8 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center text-xs transition-colors"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Saldo em Destaque */}
            <div className="p-5 bg-slate-950/60 border-b border-slate-800/80 flex items-center justify-between shrink-0">
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider block">Saldo Atual em Loja</span>
                <span className="text-2xl font-black text-emerald-400">
                  R$ {statementAccount.balance.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setStatementModalOpen(false);
                    openMovementModal(statementAccount, 'credit');
                  }}
                  className="px-3 py-1.5 rounded-xl bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-500/30 text-xs font-bold transition-all flex items-center gap-1.5"
                >
                  <i className="fas fa-plus text-[10px]"></i>
                  <span>Creditar</span>
                </button>
                <button
                  onClick={() => {
                    setStatementModalOpen(false);
                    openMovementModal(statementAccount, 'debit');
                  }}
                  disabled={statementAccount.balance <= 0}
                  className="px-3 py-1.5 rounded-xl bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 border border-amber-500/30 text-xs font-bold transition-all flex items-center gap-1.5 disabled:opacity-30"
                >
                  <i className="fas fa-minus text-[10px]"></i>
                  <span>Debitar</span>
                </button>
              </div>
            </div>

            {/* Lista de Movimentações */}
            <div className="p-5 overflow-y-auto flex-1 space-y-3">
              {loadingStatement ? (
                <div className="py-12 text-center text-slate-500">
                  <i className="fas fa-spinner fa-spin text-2xl mb-2 text-purple-400"></i>
                  <p className="text-xs">Carregando histórico do cliente...</p>
                </div>
              ) : statementMovements.length === 0 ? (
                <div className="py-12 text-center text-slate-500">
                  <i className="fas fa-receipt text-3xl mb-2 opacity-50"></i>
                  <p className="text-xs font-bold text-slate-400">Nenhuma movimentação registrada</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">Essa conta ainda não possui entradas ou saídas de crédito.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {statementMovements.map(m => {
                    const isCredit = m.movement_type === 'credit';
                    return (
                      <div
                        key={m.id}
                        className="bg-slate-950/60 border border-slate-800/80 rounded-2xl p-3.5 flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold shrink-0 ${
                              isCredit
                                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                            }`}
                          >
                            <i className={`fas ${isCredit ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
                          </div>
                          <div className="min-w-0">
                            <div className="font-bold text-xs text-white truncate">{m.reason}</div>
                            <div className="text-[11px] text-slate-500 flex items-center gap-2">
                              <span>
                                {new Date(m.created_at).toLocaleDateString('pt-BR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                              {m.creator && (
                                <span>• Operador: {m.creator.fullname || m.creator.username}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <span
                            className={`text-sm font-black ${
                              isCredit ? 'text-emerald-400' : 'text-amber-400'
                            }`}
                          >
                            {isCredit ? '+' : '-'} R$ {m.amount.toFixed(2)}
                          </span>
                          <span className="block text-[10px] text-slate-500 uppercase font-bold">
                            {isCredit ? 'Entrada' : 'Saída / Resgate'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3.5 border-t border-slate-800 bg-slate-950/60 flex items-center justify-between text-xs text-slate-500 shrink-0">
              <span>{statementMovements.length} transações registradas</span>
              <button
                onClick={() => setStatementModalOpen(false)}
                className="px-4 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL: EXTRATO GERAL DA LOJA (AUDITORIA RECENTE) */}
      {/* ========================================================= */}
      {storeAuditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/40 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">
                  <i className="fas fa-receipt"></i>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white tracking-tight">Extrato Geral de Créditos da Loja</h3>
                  <p className="text-[11px] text-slate-400">Auditoria completa de movimentações recentes de todos os clientes</p>
                </div>
              </div>
              <button
                onClick={() => setStoreAuditModalOpen(false)}
                className="w-8 h-8 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center text-xs transition-colors"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Filtros do Extrato Geral */}
            <div className="px-6 py-3 bg-slate-950/60 border-b border-slate-800/80 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setAuditFilterType('all')}
                  className={`px-3 py-1 rounded-xl text-xs font-bold transition-all ${
                    auditFilterType === 'all'
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  Todas
                </button>
                <button
                  onClick={() => setAuditFilterType('credit')}
                  className={`px-3 py-1 rounded-xl text-xs font-bold transition-all ${
                    auditFilterType === 'credit'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-800 text-slate-400 hover:text-emerald-400'
                  }`}
                >
                  Apenas Créditos (+)
                </button>
                <button
                  onClick={() => setAuditFilterType('debit')}
                  className={`px-3 py-1 rounded-xl text-xs font-bold transition-all ${
                    auditFilterType === 'debit'
                      ? 'bg-amber-600 text-white'
                      : 'bg-slate-800 text-slate-400 hover:text-amber-400'
                  }`}
                >
                  Apenas Débitos (-)
                </button>
              </div>

              <span className="text-[11px] text-slate-500 hidden sm:inline">Últimas 50 movimentações</span>
            </div>

            {/* Lista de Movimentações */}
            <div className="p-6 overflow-y-auto flex-1 space-y-2.5">
              {loadingStoreAudit ? (
                <div className="py-12 text-center text-slate-500">
                  <i className="fas fa-spinner fa-spin text-2xl mb-2 text-purple-400"></i>
                  <p className="text-xs">Carregando extrato de auditoria...</p>
                </div>
              ) : (
                (() => {
                  const filtered = storeMovements.filter(m =>
                    auditFilterType === 'all' ? true : m.movement_type === auditFilterType
                  );

                  if (filtered.length === 0) {
                    return (
                      <div className="py-12 text-center text-slate-500">
                        <i className="fas fa-clock-rotate-left text-3xl mb-2 opacity-50"></i>
                        <p className="text-xs font-bold text-slate-400">Nenhum registro encontrado</p>
                      </div>
                    );
                  }

                  return filtered.map(m => {
                    const isCredit = m.movement_type === 'credit';
                    const customer = m.credit_account?.user;
                    const customerName = customer?.fullname || customer?.username || 'Cliente';

                    return (
                      <div
                        key={m.id}
                        className="bg-slate-950/60 border border-slate-800/80 rounded-2xl p-3.5 flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold shrink-0 ${
                              isCredit
                                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                            }`}
                          >
                            <i className={`fas ${isCredit ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-xs text-white truncate">{customerName}</span>
                              {customer?.username && (
                                <span className="text-[11px] text-purple-400 font-medium truncate">
                                  @{customer.username}
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-400 truncate mt-0.5">{m.reason}</div>
                            <div className="text-[10px] text-slate-500 mt-0.5">
                              {new Date(m.created_at).toLocaleDateString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <span
                            className={`text-sm font-black ${
                              isCredit ? 'text-emerald-400' : 'text-amber-400'
                            }`}
                          >
                            {isCredit ? '+' : '-'} R$ {m.amount.toFixed(2)}
                          </span>
                          <span className="block text-[10px] text-slate-500 uppercase font-bold">
                            {isCredit ? 'Crédito' : 'Resgate'}
                          </span>
                        </div>
                      </div>
                    );
                  });
                })()
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-3.5 border-t border-slate-800 bg-slate-950/60 flex items-center justify-end shrink-0">
              <button
                onClick={() => setStoreAuditModalOpen(false)}
                className="px-4 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default ManageCustomerCredits;
